/**
 * Portal del paciente: pantalla de inicio.
 *
 * CRITERIO DE DISEÑO
 * ==================
 * Quien entra aquí puede tener poca práctica navegando, ser de zona rural o
 * leer con dificultad. La pantalla tiene que llevarlo de la mano.
 *
 * El paciente entra por UNA cosa: ver sus exámenes. Por eso la lista está en
 * pantalla desde el primer momento, sin ningún clic previo.
 *
 * La versión anterior mostraba un párrafo de bienvenida y dos tarjetas ("Mis
 * Estudios" y "Descargar Informes") que llevaban AL MISMO SITIO. Dos puertas
 * al mismo cuarto es lo más confuso que se le puede poner delante a alguien
 * que navega poco: se queda decidiendo cuál es la correcta. Y sus exámenes no
 * aparecían por ningún lado: tenía que deducir que había que hacer clic.
 *
 * Tres reglas que se siguen aquí:
 *
 * 1. La respuesta primero. Lo que el paciente vino a buscar se ve al entrar.
 * 2. Una sola acción por examen. Nada de elegir entre caminos equivalentes.
 * 3. Palabras de todos los días. "Mis exámenes", no "estudios de imágenes
 *    diagnósticas". "Listo para ver", no "FINAL".
 *
 * Los horarios de atención y los avisos van al final: son útiles, pero no son
 * el motivo de la visita.
 */

import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Clock, CheckCircle, ChevronRight } from 'lucide-react'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import { APP_ROUTES } from '@/config/constants'
import { getStoredPatientInfo } from '@/application/auth/auth.service'
import { getMyStudies, type PatientStudy } from '@/application/patient-portal'
import { aFechaSegura } from '@/lib/utils'
import { SelectorDeEntidad } from '@/presentation/components/patient-portal/SelectorDeEntidad'

/**
 * Traduce la modalidad DICOM a lo que el paciente llamaria a ese examen.
 *
 * "DX" no le dice nada a nadie fuera del gremio; "Radiografía" si.
 */
const NOMBRE_DEL_EXAMEN: Record<string, string> = {
  DX: 'Radiografía',
  CR: 'Radiografía',
  CT: 'Tomografía',
  MR: 'Resonancia magnética',
  US: 'Ecografía',
  MG: 'Mamografía',
  XA: 'Angiografía',
  NM: 'Medicina nuclear',
  PT: 'PET',
  RF: 'Fluoroscopia',
  ES: 'Endoscopia',
  OT: 'Examen',
}

const nombreDelExamen = (estudio: PatientStudy): string => {
  const base = NOMBRE_DEL_EXAMEN[estudio.modality] ?? 'Examen'
  const parte = estudio.studyDescription?.trim()
  return parte ? `${base}: ${parte}` : base
}

/** Fecha en palabras. "14 de marzo de 2026" se lee mejor que "2026-03-14". */
const fechaEnPalabras = (fecha: string): string => {
  const d = aFechaSegura(fecha)
  if (Number.isNaN(d.getTime())) return fecha
  return d.toLocaleDateString('es-CO', { day: 'numeric', month: 'long', year: 'numeric' })
}

/**
 * Estado del examen en lenguaje del paciente.
 *
 * Al paciente no le importa el flujo interno de la clínica: le importa si ya
 * puede ver su resultado o si todavía no.
 */
const estadoDelExamen = (estudio: PatientStudy): { listo: boolean; texto: string } =>
  estudio.reportId
    ? { listo: true, texto: 'Resultado listo' }
    : { listo: false, texto: 'Aún en revisión' }

export function PatientHomePage() {
  const paciente = getStoredPatientInfo()

  const { data, isLoading, error } = useQuery({
    queryKey: ['patient-studies'],
    queryFn: () => getMyStudies({ limit: 100 }),
  })

  const examenes = data?.studies ?? []

  return (
    <div className="mx-auto max-w-3xl space-y-8 px-4 py-6">
      {/* Saludo corto. Una línea, no un párrafo. */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          Hola{paciente?.firstName ? `, ${paciente.firstName}` : ''}
        </h1>
        <p className="mt-1 text-lg text-gray-600 dark:text-gray-400">Estos son sus exámenes</p>
      </div>

      {/* Si esta persona tiene ficha en varias entidades, se le dice y se le
          deja elegir. Antes se abría una por ella y el resto de su historia
          le quedaba invisible. Con una sola entidad esto no se muestra. */}
      <SelectorDeEntidad />

      {isLoading && (
        <p className="py-8 text-center text-lg text-gray-500 dark:text-gray-400">Buscando sus exámenes...</p>
      )}

      {error && (
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-lg text-gray-700 dark:text-gray-300">
              No pudimos mostrar sus exámenes en este momento.
            </p>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Intente de nuevo más tarde o comuníquese con la clínica.
            </p>
          </CardContent>
        </Card>
      )}

      {!isLoading && !error && examenes.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-lg text-gray-700 dark:text-gray-300">Todavía no tiene exámenes registrados.</p>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Cuando le hagan uno, aparecerá aquí sin que tenga que buscarlo.
            </p>
          </CardContent>
        </Card>
      )}

      {/* La lista: una fila por examen, una sola acción. Toda la fila es el
          enlace, porque un botón pequeño es difícil de acertar en un celular
          y para una persona mayor. */}
      <div className="space-y-3">
        {examenes.map((estudio) => {
          const estado = estadoDelExamen(estudio)

          return (
            <Link
              key={estudio.id}
              to={APP_ROUTES.PATIENT_PORTAL.STUDY_DETAIL(estudio.id)}
              className="block rounded-xl border border-gray-200 dark:border-gray-800 bg-white p-5 transition-colors hover:border-teal-400 hover:bg-teal-50 dark:bg-teal-950/40"
            >
              <div className="flex items-center justify-between gap-4">
                <div className="min-w-0">
                  <p className="truncate text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {nombreDelExamen(estudio)}
                  </p>
                  <p className="mt-1 text-gray-600 dark:text-gray-400">{fechaEnPalabras(estudio.studyDate)}</p>

                  <p
                    className={`mt-2 inline-flex items-center gap-1.5 text-sm font-medium ${
                      estado.listo ? 'text-green-700 dark:text-green-300' : 'text-amber-700 dark:text-amber-300'
                    }`}
                  >
                    {estado.listo ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      <Clock className="h-4 w-4" />
                    )}
                    {estado.texto}
                  </p>
                </div>

                <ChevronRight className="h-6 w-6 shrink-0 text-gray-400" />
              </div>
            </Link>
          )
        })}
      </div>

      {/* Solo queda la duda que el paciente sí tiene: cuándo estará listo.
          Los horarios de atención se quitaron porque estaban escritos en el
          código, no venían de los datos de la IPS: habrían sido falsos para
          casi cualquier clínica, y un horario equivocado es peor que ninguno.
          Si algún día se muestran, tendrán que salir de la institución. */}
      {examenes.some((e) => !e.reportId) && (
        <p className="border-t border-gray-200 dark:border-gray-800 pt-6 text-sm text-gray-600 dark:text-gray-400">
          Los resultados suelen estar listos entre 48 y 72 horas después del
          examen. No tiene que hacer nada: cuando esté listo aparecerá aquí.
        </p>
      )}
    </div>
  )
}
